'use client';

import type { StaticImageData } from 'next/image';
import type { ReactNode } from 'react';
import React from 'react';
import { cx } from 'class-variance-authority';
import Image from 'next/image';

import { Button } from '@zealy/design-system';
import { useDidMount } from '@zealy/design-system/src/hooks/useDidMount';
import { ArrowRightLine } from '@zealy/icons';
import { useAllTradingCompetitions } from '@zealy/queries';
import { JSONSafeParse } from '@zealy/utils';

import RawCarousel from '#components/Carousel/RawCarousel';
import { useCommunitiesFlags } from '#context/FeatureFlags';

import blue from './assets/blue.webp';
import plant from './assets/plant.webp';

export const FeatureCard = ({
  title,
  description,
  image = blue,
  href,
  className,
  showIllustration = false,
  rightImage,
  displayCta = true,
}: {
  title: ReactNode;
  description: string;
  image?: string | StaticImageData;
  href: string;
  className?: string;
  showIllustration?: boolean;
  rightImage?: string;
  displayCta?: boolean;
}) => (
  <a
    href={href}
    className={cx(
      'block rounded-component-md h-[244px] w-[450px] max-w-[90vw] relative hover:blur-child transition-transform hover:scale-[1.005] duration-200 ease-out overflow-hidden border border-separators',
      className,
    )}
  >
    <div className="max-w-[330px] relative z-10 px-300 sm:px-500 py-400 sm:py-500">
      <h4 className="heading-3 text-primary w-full">{title}</h4>
      <p className="body-lg mt-200 mb-100 w-full text-white/90 line-clamp-2">{description}</p>
      {displayCta && (
        <Button variant="ghost" rightIcon={<ArrowRightLine />} className="-translate-x-200">
          Learn more
        </Button>
      )}
    </div>

    {showIllustration && (
      <Image
        src={plant}
        alt=""
        width={200}
        height={94}
        loading="lazy"
        decoding="async"
        className="select-none pointer-events-none absolute top-100 right-200 saturate-150 hidden sm:block md:hidden lg:block z-10 object-contain"
      />
    )}

    {rightImage && (
      <Image
        src={rightImage}
        alt={`${title} right image`}
        width={64}
        height={64}
        className="absolute right-400 lg:top-500 bottom-200 flex items-center justify-center rounded-3xl aspect-square max-h-[88px] max-w-[88px] min-h-[64px] min-w-[64px] overflow-clip z-10"
      />
    )}

    <Image
      src={image}
      alt=""
      fill
      className="select-none pointer-events-none object-fill rounded-component-md blurred transition-all duration-300 ease-in-out z-0"
    />

    <Image
      src={image}
      alt=""
      fill
      className="select-none pointer-events-none object-fill rounded-component-md z-0 saturate-150"
    />
  </a>
);

interface LandingBanner {
  title: string;
  image: string;
  url: string;
  showIllustration: boolean;
  description: string;
  rightImage?: string;
  displayCta?: boolean;
}

export const Featured = () => {
  const { landing_top_banners } = useCommunitiesFlags(['landing_top_banners']);
  const landingBanners = JSONSafeParse(
    (landing_top_banners?.value as string) || '[]',
  ) as LandingBanner[];

  const { data: tradingCompetitions } = useAllTradingCompetitions();
  const didMount = useDidMount();

  if (!didMount) return null;

  // Create all feature cards including fallback ones
  const allFeatureCards: React.ReactElement[] = [];

  // Add active trading competitions
  if (tradingCompetitions) {
    const now = new Date();
    const activeCompetitions = tradingCompetitions.filter(competition => {
      const startDate = new Date(competition.startDate);
      const endDate = new Date(competition.endDate);
      return now >= startDate && now <= endDate && competition.banner;
    });

    activeCompetitions.forEach(competition => {
      allFeatureCards.push(
        <FeatureCard
          key={`trading-competition-${competition.id}`}
          href={`${process.env.NEXT_PUBLIC_APP_URL}/cw/${competition.communitySubdomain}/trade/${competition.id}`}
          showIllustration={false}
          displayCta={false}
          image={competition.banner!}
          title=""
          description=""
        />,
      );
    });
  }

  // Add landing banners
  landingBanners.forEach((banner, index) => {
    const titleSplit = banner.title.split('<br />');
    allFeatureCards.push(
      <FeatureCard
        key={`landing-top-banner-${index}`}
        title={
          <>
            {titleSplit.map((line, titleSplitIndex) => (
              <React.Fragment key={titleSplitIndex}>
                {titleSplitIndex < titleSplit.length - 1 ? (
                  <>
                    {line}
                    <br />
                  </>
                ) : (
                  line
                )}
              </React.Fragment>
            ))}
          </>
        }
        description={banner.description}
        image={banner.image}
        href={banner.url}
        showIllustration={banner.showIllustration}
        rightImage={banner.rightImage}
      />,
    );
  });

  return (
    <div className="w-full pt-400 pb-200  mx-auto px-100">
      <RawCarousel.Carousel step={1}>
        <div className="flex justify-end">
          <div className="gap-100 hidden md:flex">
            <RawCarousel.CarouselPrevious />
            <RawCarousel.CarouselNext />
          </div>
        </div>
        <RawCarousel.CarouselContent>
          {/* <FeaturedAd key="hypelab-native-ad" placement="643e79ccdf" /> */}
          <RawCarousel.CarouselItem>
            <FeatureCard
              href={'https://zealy.io/cw/datahaven/questboard'}
              showIllustration={false}
              displayCta={false}
              image={'/nstatic/campaigns/datahaven/banner.webp'}
              title=" "
              description=" "
            />
          </RawCarousel.CarouselItem>
          <RawCarousel.CarouselItem>
            <FeatureCard
              href={'https://zealy.io/cw/leagueoftraders/questboard'}
              showIllustration={false}
              displayCta={false}
              image={'/nstatic/campaigns/league-of-traders/banner.webp'}
              title=" "
              description=" "
            />
          </RawCarousel.CarouselItem>
          {allFeatureCards.map((card, index) => (
            <RawCarousel.CarouselItem key={`featured-card-${index}`}>
              {card}
            </RawCarousel.CarouselItem>
          ))}
        </RawCarousel.CarouselContent>
      </RawCarousel.Carousel>
    </div>
  );
};
